import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Drawer, Box, Typography, IconButton, Chip, Tabs, Tab, Divider, Button,
  List, ListItemButton, ListItemText, CircularProgress, Alert, Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import PhotoLibraryOutlinedIcon from "@mui/icons-material/PhotoLibraryOutlined";
import FormatListNumberedRoundedIcon from "@mui/icons-material/FormatListNumberedRounded";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { useGetHelpContextQuery, HelpArticleFull, HelpImage } from "../../Api/helpApi";
import { API_URL } from "../../config";

type Props = { open: boolean; onClose: () => void; route: string };

const mediaUrl = (path: string): string => {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  const base = String(API_URL || "").replace(/\/+$/, "");
  return `${base}/help/media/${path.replace(/^\/+/, "")}`;
};

const HelpDrawer: React.FC<Props> = ({ open, onClose, route }) => {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useGetHelpContextQuery(route, { skip: !open });
  const article: HelpArticleFull | null = data?.data?.article ?? null;
  const candidates = data?.data?.candidates ?? [];

  const tabs = useMemo(() => {
    if (!article) return [] as { key: string; label: string; icon: JSX.Element }[];
    const t: { key: string; label: string; icon: JSX.Element }[] = [];
    if (article.quick_steps?.length) t.push({ key: "quick", label: "Quick Guide", icon: <FormatListNumberedRoundedIcon fontSize="small" /> });
    if (article.images?.length)      t.push({ key: "images", label: "Screenshots", icon: <PhotoLibraryOutlinedIcon fontSize="small" /> });
    if (article.video_url || article.video_storage_path) t.push({ key: "video", label: "Video", icon: <PlayCircleOutlineIcon fontSize="small" /> });
    if (article.body_markdown)        t.push({ key: "full", label: "Full Guide", icon: <ArticleOutlinedIcon fontSize="small" /> });
    return t;
  }, [article]);

  const [tab, setTab] = useState(0);
  React.useEffect(() => { setTab(0); }, [article?.slug]);

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: "100%", sm: 480 }, maxWidth: "100vw" } }}>
      <Box sx={{ p: 2.5, display: "flex", alignItems: "flex-start", gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {article ? (
            <>
              <Stack direction="row" spacing={1} sx={{ mb: 0.5, flexWrap: "wrap" }}>
                <Chip size="small" label={article.category} color="primary" variant="outlined" />
                <Chip size="small" label={article.module} variant="outlined" />
              </Stack>
              <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.25 }}>{article.title}</Typography>
              {article.summary && (
                <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>{article.summary}</Typography>
              )}
            </>
          ) : (
            <Typography variant="h6" sx={{ fontWeight: 800 }}>Help</Typography>
          )}
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </Box>
      <Divider />

      <Box sx={{ p: 2.5, overflowY: "auto", flex: 1 }}>
        {isLoading && (
          <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress size={22} /></Stack>
        )}
        {isError && (
          <Alert severity="error" sx={{ fontSize: 13 }}>Could not load help content.</Alert>
        )}

        {!isLoading && !article && (
          <Stack spacing={2}>
            <Alert severity="info" sx={{ fontSize: 13 }}>
              No article is mapped to this screen yet. Try the Help Center for related topics.
            </Alert>
            {candidates.length > 0 && (
              <>
                <Typography variant="caption" sx={{ fontWeight: 700, color: "text.disabled", letterSpacing: 0.6 }}>
                  RELATED
                </Typography>
                <List dense disablePadding>
                  {candidates.slice(0, 6).map((c) => (
                    <ListItemButton
                      key={c.id}
                      onClick={() => { navigate(`/help-center/${c.slug}`); onClose(); }}
                      sx={{ borderRadius: 1.5, mb: 0.5 }}
                    >
                      <ListItemText primary={c.title} secondary={c.category} primaryTypographyProps={{ fontSize: 13.5, fontWeight: 600 }} />
                    </ListItemButton>
                  ))}
                </List>
              </>
            )}
            <Button variant="outlined" size="small" onClick={() => { navigate("/help-center"); onClose(); }} startIcon={<OpenInNewIcon />}>
              Open Help Center
            </Button>
          </Stack>
        )}

        {article && (
          <>
            {tabs.length > 1 && (
              <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" sx={{ mb: 2, minHeight: 36 }}>
                {tabs.map((t) => <Tab key={t.key} icon={t.icon} iconPosition="start" label={t.label} sx={{ minHeight: 36, textTransform: "none", fontWeight: 700, fontSize: 13 }} />)}
              </Tabs>
            )}

            {tabs[tab]?.key === "quick" && <QuickGuide steps={article.quick_steps} />}
            {tabs[tab]?.key === "images" && <ScreenshotViewer images={article.images} />}
            {tabs[tab]?.key === "video" && <VideoPlayer article={article} />}
            {tabs[tab]?.key === "full" && <FullGuide markdown={article.body_markdown || ""} />}
          </>
        )}
      </Box>

      {article && (
        <Box sx={{ p: 2, borderTop: 1, borderColor: "divider", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="caption" sx={{ color: "text.disabled" }}>
            {article.updated_at ? `Updated ${new Date(article.updated_at).toLocaleDateString()}` : ""}
          </Typography>
          <Button size="small" endIcon={<OpenInNewIcon />} onClick={() => { navigate(`/help-center/${article.slug}`); onClose(); }} sx={{ textTransform: "none", fontWeight: 700 }}>
            Open in Help Center
          </Button>
        </Box>
      )}
    </Drawer>
  );
};

const QuickGuide: React.FC<{ steps: HelpArticleFull["quick_steps"] }> = ({ steps }) => (
  <Box component="ol" sx={{ pl: 0, m: 0, listStyle: "none" }}>
    {steps.map((s) => (
      <Box key={s.number} sx={{ display: "flex", gap: 1.5, mb: 1.5, alignItems: "flex-start" }}>
        <Box sx={{
          width: 26, height: 26, borderRadius: "50%", flexShrink: 0,
          bgcolor: "primary.main", color: "primary.contrastText",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 12, fontWeight: 800,
        }}>
          {s.number}
        </Box>
        <Box sx={{ pt: 0.25 }}>
          {s.title && <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>{s.title}</Typography>}
          <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.6 }}>{s.description}</Typography>
        </Box>
      </Box>
    ))}
  </Box>
);

const ScreenshotViewer: React.FC<{ images: HelpImage[] }> = ({ images }) => {
  const [idx, setIdx] = useState(0);
  const sorted = useMemo(() => [...images].sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0)), [images]);
  if (!sorted.length) return null;
  const img = sorted[idx];
  const src = mediaUrl(img.path);
  return (
    <Box>
      <Box sx={{ position: "relative", borderRadius: 2, overflow: "hidden", border: 1, borderColor: "divider", bgcolor: "background.default" }}>
        <Box component="img" src={src} alt={img.caption ?? ""} sx={{ display: "block", width: "100%", height: "auto" }} />
        {(img.markers ?? []).map((m) => (
          <Box key={m.number} sx={{
            position: "absolute", left: `${m.x * 100}%`, top: `${m.y * 100}%`,
            transform: "translate(-50%,-50%)",
            width: 26, height: 26, borderRadius: "50%",
            bgcolor: "warning.main", color: "warning.contrastText",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 12, fontWeight: 800, boxShadow: 3, border: "2px solid white",
          }}>
            {m.number}
          </Box>
        ))}
      </Box>
      {img.caption && <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>{img.caption}</Typography>}
      {(img.markers ?? []).length > 0 && (
        <Box component="ol" sx={{ pl: 2.5, mt: 1 }}>
          {[...(img.markers ?? [])].sort((a, b) => a.number - b.number).map((m) => (
            <Typography key={m.number} component="li" variant="body2" sx={{ color: "text.secondary", mb: 0.5 }}>{m.text}</Typography>
          ))}
        </Box>
      )}
      {sorted.length > 1 && (
        <Stack direction="row" spacing={1} justifyContent="space-between" sx={{ mt: 1.5 }}>
          <Button size="small" disabled={idx === 0} onClick={() => setIdx((i) => i - 1)}>Previous</Button>
          <Typography variant="caption" sx={{ alignSelf: "center", color: "text.disabled" }}>{idx + 1} / {sorted.length}</Typography>
          <Button size="small" disabled={idx === sorted.length - 1} onClick={() => setIdx((i) => i + 1)}>Next</Button>
        </Stack>
      )}
    </Box>
  );
};

const VideoPlayer: React.FC<{ article: HelpArticleFull }> = ({ article }) => {
  const src = article.video_url ? article.video_url : (article.video_storage_path ? mediaUrl(article.video_storage_path) : "");
  const isExternalEmbed = /youtube\.com|youtu\.be|vimeo\.com/i.test(src);
  if (!src) return null;
  if (isExternalEmbed) {
    return <Box sx={{ position: "relative", pt: "56.25%" }}>
      <Box component="iframe" src={src} title={article.title} sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0, borderRadius: 2 }} allow="autoplay; encrypted-media; picture-in-picture" />
    </Box>;
  }
  return <Box component="video" controls src={src} sx={{ width: "100%", borderRadius: 2, bgcolor: "black" }} />;
};

const FullGuide: React.FC<{ markdown: string }> = ({ markdown }) => (
  <Box sx={{ "& p": { color: "text.secondary", lineHeight: 1.7, mb: 1.25 }, "& ul,& ol": { pl: 3, mb: 1.25 }, "& li": { mb: 0.5 }, "& code": { px: 0.5, py: 0.2, borderRadius: 0.5, bgcolor: "action.hover", fontSize: "0.9em" }, "& strong": { color: "text.primary" } }}>
    <ReactMarkdown remarkPlugins={[remarkGfm]}>{markdown}</ReactMarkdown>
  </Box>
);

export default HelpDrawer;
